import WebVisitsReportViewEnum from '@/Enums/Attribution/WebVisits/ReportView.enum';
import WebVisitsReportingRequest from '@/Enums/Attribution/WebVisits/ReportingRequest.enum';
import MapScale from '@/Enums/Map/MapScale.enum';
import GoogleReportRequest from '@/Enums/ReportingDashboard/ReportRequest/GoogleReportRequest.enum';
import NeuronReportRequest from '@/Enums/ReportingDashboard/ReportRequest/NeuronReportRequest.enum';
import NeuronReportView from '@/Enums/ReportingDashboard/ReportView/NeuronReportView.enum';
import ReportView from '@/Enums/ReportingDashboard/ReportView/ReportView.enum';
import {
  getWebVisitsCampaignNameData,
  getWebVisitsCityData,
  getWebVisitsCongressionalDistrictsData,
  getWebVisitsCountyData,
  getWebVisitsCreativeData,
  getWebVisitsDateData,
  getWebVisitsDayOfWeekData,
  getWebVisitsDaypartData,
  getWebVisitsDeviceData,
  getWebVisitsDigitalChannelData,
  getWebVisitsDmaData,
  getWebVisitsPixelData,
  getWebVisitsPublisherNameData,
  getWebVisitsStateData,
  getWebVisitsZipData,
} from '@/store/slices/attribution/beeswaxThunks';
import {
  getGoogleAdvertiserData,
  getGoogleAppUrlReport,
  getGoogleCampaignData,
  getGoogleChannelData,
  getGoogleCreativeData,
  getGoogleDateData,
  getGoogleDaypartData,
  getGoogleDaysOfWeekData,
  getGoogleDeviceData,
  getGoogleDmaData,
  getGooglePlacementData,
  getGooglePublisherData,
  getGoogleStateData,
} from '@/store/slices/reportingDashboard/google.thunks';
import { getNeuronReportData } from '@/store/slices/reportingDashboard/neuronSlice';
import { AsyncThunk } from '@reduxjs/toolkit';

type ActionSet<TReportRequest> = {
  action: AsyncThunk<any, any, any>;
  reportRequest: TReportRequest;
};
type ReportDispatch<TReportType extends keyof any = string, TReportRequest extends keyof any = string> = Record<
  TReportType,
  ActionSet<TReportRequest>
>;

type GoogleBaseReports = Exclude<ReportView, ReportView.Deals>;
type GoogleGeoReports = Extract<MapScale, MapScale.State | MapScale.Dma>;
type GoogleReports = GoogleBaseReports & GoogleGeoReports;
type GoogleReportDispatch = ReportDispatch<GoogleReports, GoogleReportRequest>;

type NeuronBaseReports = Exclude<
  ReportView,
  ReportView.Channel | ReportView.Device | ReportView.Daypart | ReportView.Deals
>;
type NeuronReports = NeuronBaseReports & NeuronReportView;
type NeuronReportDispatch = ReportDispatch<NeuronReports, NeuronReportRequest>;

type WebVisitsBaseReports = Exclude<ReportView, ReportView.Deals | ReportView.Advertiser>;
type WebVisitReports = WebVisitsBaseReports & WebVisitsReportViewEnum;
type WebVisitsReportDispatch = ReportDispatch<WebVisitReports, WebVisitsReportingRequest>;

export const googleReportDispatch: GoogleReportDispatch = {
  [ReportView.Date]: {
    action: getGoogleDateData,
    reportRequest: GoogleReportRequest.Date,
  },
  [ReportView.Channel]: {
    action: getGoogleChannelData,
    reportRequest: GoogleReportRequest.Channel,
  },
  [ReportView.Device]: {
    action: getGoogleDeviceData,
    reportRequest: GoogleReportRequest.Device,
  },
  [ReportView.Advertiser]: {
    action: getGoogleAdvertiserData,
    reportRequest: GoogleReportRequest.Advertiser,
  },
  [ReportView.Campaign]: {
    action: getGoogleCampaignData,
    reportRequest: GoogleReportRequest.Campaign,
  },
  [ReportView.Placement]: {
    action: getGooglePlacementData,
    reportRequest: GoogleReportRequest.Placement,
  },
  [ReportView.Creative]: {
    action: getGoogleCreativeData,
    reportRequest: GoogleReportRequest.Creative,
  },
  [ReportView.Publisher]: {
    action: getGooglePublisherData,
    reportRequest: GoogleReportRequest.Publisher,
  },
  [ReportView.App]: {
    action: getGoogleAppUrlReport,
    reportRequest: GoogleReportRequest.AppUrl,
  },
  [ReportView.DaysOfWeek]: {
    action: getGoogleDaysOfWeekData,
    reportRequest: GoogleReportRequest.DayOfWeek,
  },
  [ReportView.Daypart]: {
    action: getGoogleDaypartData,
    reportRequest: GoogleReportRequest.Daypart,
  },
  [MapScale.State]: {
    action: getGoogleStateData,
    reportRequest: GoogleReportRequest.State,
  },
  [MapScale.Dma]: {
    action: getGoogleDmaData,
    reportRequest: GoogleReportRequest.Dma,
  },
};

const commonNeuronReportDispatchValue: ActionSet<NeuronReportRequest> = {
  action: getNeuronReportData,
  reportRequest: NeuronReportRequest.Report,
};

export const neuronReportDispatch: NeuronReportDispatch = {
  [ReportView.Date]: commonNeuronReportDispatchValue,
  [ReportView.Advertiser]: commonNeuronReportDispatchValue,
  [ReportView.Campaign]: commonNeuronReportDispatchValue,
  [ReportView.Placement]: commonNeuronReportDispatchValue,
  [ReportView.Creative]: commonNeuronReportDispatchValue,
  [ReportView.Publisher]: commonNeuronReportDispatchValue,
  [NeuronReportView.VenueType]: commonNeuronReportDispatchValue,
};

export const webVisitsReportDispatch: WebVisitsReportDispatch = {
  [WebVisitsReportViewEnum.Date]: {
    action: getWebVisitsDateData,
    reportRequest: WebVisitsReportingRequest.Date,
  },
  [WebVisitsReportViewEnum.Device]: {
    action: getWebVisitsDeviceData,
    reportRequest: WebVisitsReportingRequest.Device,
  },
  [WebVisitsReportViewEnum.Channel]: {
    action: getWebVisitsDigitalChannelData,
    reportRequest: WebVisitsReportingRequest.Channel,
  },
  [WebVisitsReportViewEnum.Campaign]: {
    action: getWebVisitsCampaignNameData,
    reportRequest: WebVisitsReportingRequest.Campaign,
  },
  [WebVisitsReportViewEnum.Creative]: {
    action: getWebVisitsCreativeData,
    reportRequest: WebVisitsReportingRequest.Creative,
  },
  [WebVisitsReportViewEnum.Publisher]: {
    action: getWebVisitsPublisherNameData,
    reportRequest: WebVisitsReportingRequest.Publisher,
  },
  [WebVisitsReportViewEnum.Daypart]: {
    action: getWebVisitsDaypartData,
    reportRequest: WebVisitsReportingRequest.Daypart,
  },
  [WebVisitsReportViewEnum.DaysOfWeek]: {
    action: getWebVisitsDayOfWeekData,
    reportRequest: WebVisitsReportingRequest.DayOfWeek,
  },
  [WebVisitsReportViewEnum.Pixel]: {
    action: getWebVisitsPixelData,
    reportRequest: WebVisitsReportingRequest.Pixel,
  },
  [MapScale.State]: {
    action: getWebVisitsStateData,
    reportRequest: WebVisitsReportingRequest.State,
  },
  [MapScale.County]: {
    action: getWebVisitsCountyData,
    reportRequest: WebVisitsReportingRequest.County,
  },
  [MapScale.Zip]: {
    action: getWebVisitsZipData,
    reportRequest: WebVisitsReportingRequest.Zip,
  },
  [MapScale.CongressionalDistricts]: {
    action: getWebVisitsCongressionalDistrictsData,
    reportRequest: WebVisitsReportingRequest.Congressional,
  },
  [MapScale.Dma]: {
    action: getWebVisitsDmaData,
    reportRequest: WebVisitsReportingRequest.Dma,
  },
  [MapScale.City]: {
    action: getWebVisitsCityData,
    reportRequest: WebVisitsReportingRequest.City,
  },
};
